// Marketing-site widgets adapted from the dashboard mockup (now in the
// ScopeWorth.DesignSystem repo, web/dashboard/).
// Visual language (dark surface, slate scale, electric blue / teal / amber
// / red / purple accents, tabular numerics, Inter throughout) mirrors its
// views-*.jsx so the commercial site previews what the future product will
// look like.
//
// All component class names are prefixed `swd-` to avoid colliding with the
// internal dashboard `.dash-*` classes used by the prototypes.
import React from 'react';
import { localizedHref } from './i18n/bootstrap.js';
import { t as tx } from './i18n/useT.js';
import { IntegrationLogo } from './Integrations.jsx';
import { getCatalogIntegration } from './integration-catalog.js';

function previewIntegration(name, kind, meta) {
  const integration = getCatalogIntegration(name);
  if (!integration) throw new Error(`Unknown integration preview: ${name}`);
  return {
    id: integration.id,
    name: integration.name,
    kind,
    meta,
    evidenceFlag: integration.previewEvidence,
    // Preserve the existing presentation classes; the evidence flag above is
    // the claim-bearing value and is asserted by the catalogue contract.
    status: integration.previewEvidence === 'example_connected' ? 'example' : 'limited',
  };
}

// ---------- viewport-triggered animation ----------
// Returns [ref, t, started] where t eases from 0 → 1 once the element first
// scrolls into view. Respects prefers-reduced-motion (t snaps to 1).
export function useInViewAnim({ duration = 1300, threshold = 0.18 } = {}) {
  const ref = React.useRef(null);
  // Emit meaningful final sample values before JavaScript starts.
  const [t, setT] = React.useState(1);
  const [started, setStarted] = React.useState(false);
  const fired = React.useRef(false);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduced = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) { setT(1); setStarted(true); return; }

    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !fired.current) {
          fired.current = true;
          io.disconnect();
          setStarted(true);
          let raf, start;
          const step = (ts) => {
            if (!start) start = ts;
            const p = Math.min(1, (ts - start) / duration);
            // easeOutCubic
            setT(1 - Math.pow(1 - p, 3));
            if (p < 1) raf = requestAnimationFrame(step);
          };
          raf = requestAnimationFrame(step);
        }
      });
    }, { threshold });
    io.observe(el);
    return () => io.disconnect();
  }, [duration, threshold]);

  return [ref, t, started];
}

// Animates the numeric portion of a display string. Handles prefix/suffix
// (e.g. "$44,800", "+19h", "+24% vs planned", "31") so we don't need
// per-card formatters.
export function tweenValueString(raw, t) {
  if (raw == null) return raw;
  const s = String(raw);
  const m = s.match(/([\d][\d,.]*\d|\d)/);
  if (!m) return s;
  const numStr = m[0];
  const idx = m.index;
  const hasComma = numStr.includes(',');
  const hasDot = numStr.includes('.');
  const target = parseFloat(numStr.replace(/,/g, ''));
  if (!isFinite(target)) return s;
  const decimals = hasDot ? (numStr.split('.')[1]?.length || 1) : 0;
  const current = target * t;
  let formatted = current.toLocaleString('en-GB', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: hasComma,
  });
  return s.slice(0, idx) + formatted + s.slice(idx + numStr.length);
}

// ---------- shared sample values (kept in lock-step with skills.md §9) ----------
const SAMPLE = {
  client: 'Farhaven Industries',
  agency: 'Northwind Studios',
  project: 'Colony Ops Console',
  period: tx('swd.period'),
  scopeLock: 'Apr 02',
  kpis: [
    { id: 'scope',   label: tx('swd.kpis.scope.label'),   value: '$8,600',  delta: tx('swd.kpis.scope.delta'),   status: 'risk', sub: tx('swd.kpis.scope.sub') },
    { id: 'shipped', label: tx('swd.kpis.shipped.label'), value: '31',      delta: tx('swd.kpis.shipped.delta'), status: 'ok',   sub: tx('swd.kpis.shipped.sub') },
    { id: 'invest',  label: tx('swd.kpis.invest.label'),  value: '$44,800', delta: tx('swd.kpis.invest.delta'),  status: 'warn', sub: tx('swd.kpis.invest.sub') },
    { id: 'ai',      label: tx('swd.kpis.ai.label'),      value: '+19h',    delta: tx('swd.kpis.ai.delta'),      status: 'ai',   sub: tx('swd.kpis.ai.sub') },
  ],
  flow: {
    inputs: [
      { id: 'jira', label: tx('swd.flow.inputs.jira'), count: tx('swd.flow.inputs.jiraCount'), color: 'blue'   },
      { id: 'gh',   label: tx('swd.flow.inputs.gh'),   count: tx('swd.flow.inputs.ghCount'),   color: 'blue'   },
      { id: 'cal',  label: tx('swd.flow.inputs.cal'),  count: tx('swd.flow.inputs.calCount'),  color: 'purple' },
      { id: 'ai',   label: tx('swd.flow.inputs.ai'),   count: 'Cursor · Copilot',             color: 'purple' },
      { id: 'rel',  label: tx('swd.flow.inputs.rel'),  count: tx('swd.flow.inputs.relCount'),  color: 'teal'   },
    ],
    buckets: [
      { id: 'feat', label: tx('swd.flow.buckets.feat'), value: '$28,400', pct: 63, color: 'blue'   },
      { id: 'rev',  label: tx('swd.flow.buckets.rev'),  value: '$4,300',  pct: 10, color: 'blue'   },
      { id: 'rwk',  label: tx('swd.flow.buckets.rwk'),  value: '$3,900',  pct: 9,  color: 'red'    },
      { id: 'mtg',  label: tx('swd.flow.buckets.mtg'),  value: '$5,600',  pct: 12, color: 'purple' },
      { id: 'oth',  label: tx('swd.flow.buckets.oth'),  value: '$2,600',  pct: 6,  color: 'amber'  },
    ],
    output: { label: tx('swd.flow.outputLabel'), value: '$44,800', sub: tx('swd.period') },
  },
  insights: [
    { kind: 'risk', body: tx('swd.insights.0.body'), meta: tx('swd.insights.0.meta') },
    { kind: 'warn', body: tx('swd.insights.1.body'), meta: tx('swd.insights.1.meta') },
    { kind: 'ai',   body: tx('swd.insights.2.body'), meta: tx('swd.insights.2.meta') },
    { kind: 'ok',   body: tx('swd.insights.3.body'), meta: tx('swd.insights.3.meta') },
  ],
  proof: {
    objective: { t: tx('swd.proof.objectiveT'), meta: tx('swd.proof.objectiveMeta') },
    epics: [
      { id: 'ep-1', t: 'Console authentication',   meta: '6 issues · 4 PRs · v2.6', status: 'ok'   },
      { id: 'ep-2', t: 'Operator settings',        meta: '7 issues · 5 PRs · v2.7', status: 'warn', note: 'AC changed mid cycle' },
      { id: 'ep-3', t: 'Telemetry export',         meta: '3 issues · 2 PRs · v2.6', status: 'ok'   },
      { id: 'ep-4', t: 'Alert routing',            meta: '8 issues · 6 PRs · v2.9', status: 'risk', note: 'Added after scope lock' },
      { id: 'ep-5', t: 'Performance improvements', meta: '7 issues · 6 PRs · v2.8', status: 'ok'   },
    ],
    items: [
      { t: 'Login: TOTP fallback',      meta: 'COPS 310 · Apr 06', status: 'ok'   },
      { t: 'Settings: operator contacts', meta: 'COPS 388 · Apr 14', status: 'warn' },
      { t: 'Telemetry: CSV export bug', meta: 'COPS 401 · Apr 17', status: 'ok'   },
      { t: 'Alerts: email digest',      meta: 'COPS 412 · Apr 22', status: 'risk' },
      { t: 'Perf: cache static SSR',    meta: 'COPS 451 · Apr 28', status: 'ok'   },
    ],
    outcomes: [
      { t: tx('swd.proof.outcomes.0.t'), meta: tx('swd.proof.outcomes.0.meta') },
      { t: tx('swd.proof.outcomes.1.t'), meta: tx('swd.proof.outcomes.1.meta') },
      { t: tx('swd.proof.outcomes.2.t'), meta: tx('swd.proof.outcomes.2.meta') },
    ],
  },
  timeline: [
    { date: 'Apr 02', kind: 'plan',   tag: tx('swd.timeline.events.0.tag'), title: tx('swd.timeline.events.0.title'), desc: tx('swd.timeline.events.0.desc') },
    { date: 'Apr 05', kind: 'add',    tag: tx('swd.timeline.events.1.tag'), title: tx('swd.timeline.events.1.title'), desc: tx('swd.timeline.events.1.desc'), cost: '+$2,100' },
    { date: 'Apr 09', kind: 'clar',   tag: tx('swd.timeline.events.2.tag'), title: tx('swd.timeline.events.2.title'), desc: tx('swd.timeline.events.2.desc') },
    { date: 'Apr 12', kind: 'rework', tag: tx('swd.timeline.events.3.tag'), title: tx('swd.timeline.events.3.title'), desc: tx('swd.timeline.events.3.desc'), cost: '+$800' },
    { date: 'Apr 18', kind: 'ship',   tag: tx('swd.timeline.events.4.tag'), title: tx('swd.timeline.events.4.title'), desc: tx('swd.timeline.events.4.desc') },
    { date: 'Apr 26', kind: 'add',    tag: tx('swd.timeline.events.5.tag'), title: tx('swd.timeline.events.5.title'), desc: tx('swd.timeline.events.5.desc'), cost: '+$3,800' },
    { date: 'Apr 30', kind: 'ship',   tag: tx('swd.timeline.events.6.tag'), title: tx('swd.timeline.events.6.title'), desc: tx('swd.timeline.events.6.desc') },
  ],
  waterfall: [
    { id: 'plan', label: tx('swd.waterfall.barPlan'), value: 36200, kind: 'base'  },
    { id: 'add',  label: tx('swd.waterfall.barAdd'),  value: 5900,  kind: 'add'   },
    { id: 'rw',   label: tx('swd.waterfall.barRw'),   value: 1500,  kind: 'add'   },
    { id: 'rev',  label: tx('swd.waterfall.barRev'),  value: 800,   kind: 'add'   },
    { id: 'mtg',  label: tx('swd.waterfall.barMtg'),  value: 400,   kind: 'add'   },
    { id: 'tot',  label: tx('swd.waterfall.barTot'),  value: 44800, kind: 'total' },
  ],
  invoiceTable: [
    { driver: tx('swd.costTable.rows.0.driver'), evidence: tx('swd.costTable.rows.0.evidence'), impact: '$5,900', explain: tx('swd.costTable.rows.0.explain') },
    { driver: tx('swd.costTable.rows.1.driver'), evidence: tx('swd.costTable.rows.1.evidence'), impact: '$1,500', explain: tx('swd.costTable.rows.1.explain') },
    { driver: tx('swd.costTable.rows.2.driver'), evidence: tx('swd.costTable.rows.2.evidence'), impact: '$400',   explain: tx('swd.costTable.rows.2.explain') },
    { driver: tx('swd.costTable.rows.3.driver'), evidence: tx('swd.costTable.rows.3.evidence'), impact: '$800',   explain: tx('swd.costTable.rows.3.explain') },
  ],
  ai: {
    cycleAi:    [4.1, 3.8, 3.2, 3.4, 2.9, 2.8, 3.1, 2.6, 2.4, 2.8, 2.3, 2.1, 2.4, 2.0],
    cycleNonAi: [4.0, 4.2, 4.1, 4.3, 4.4, 4.0, 4.1, 4.3, 4.2, 4.4, 4.1, 4.0, 4.2, 4.1],
    review: [
      { l: 'UI changes',      ai: 18, base: 12 },
      { l: 'API changes',     ai: 24, base: 14 },
      { l: 'Refactors',       ai: 30, base: 16 },
      { l: 'Bug fixes',       ai: 12, base: 10 },
      { l: 'Generated tests', ai: 36, base: 18 },
    ],
  },
  confidence: {
    score: 74,
    label: tx('swd.confidence.label'),
    health: [
      { t: tx('swd.confidence.health.0.t'), pct: 82, status: 'ok',   note: tx('swd.confidence.health.0.note') },
      { t: tx('swd.confidence.health.1.t'), pct: 64, status: 'warn', note: tx('swd.confidence.health.1.note') },
      { t: tx('swd.confidence.health.2.t'), pct: 39, status: 'risk', note: tx('swd.confidence.health.2.note') },
      { t: tx('swd.confidence.health.3.t'), pct: 60, status: 'warn', note: tx('swd.confidence.health.3.note') },
      { t: tx('swd.confidence.health.4.t'), pct: 55, status: 'warn', note: tx('swd.confidence.health.4.note') },
    ],
  },
  integrations: [
    previewIntegration('Jira', tx('swd.connected.kinds.jira'), tx('swd.connected.meta.jira')),
    previewIntegration('GitHub', tx('swd.connected.kinds.github'), tx('swd.connected.meta.github')),
    previewIntegration('Google Calendar', tx('swd.connected.kinds.cal'), tx('swd.connected.meta.cal')),
    previewIntegration('Cursor', tx('swd.connected.kinds.cursor'), tx('swd.connected.meta.cursor')),
    previewIntegration('GitHub Copilot', tx('swd.connected.kinds.copilot'), tx('swd.connected.meta.copilot')),
    previewIntegration('GitLab', tx('swd.connected.kinds.gitlab'), tx('swd.connected.meta.gitlab')),
  ],

  // ---------- Capacity Cost Simulator (ported from web/simulator/data.js in ScopeWorth.DesignSystem) ----------
  simulator: {
    scenarios: [
      { id: 's-base', name: tx('swd.scenario.items.0.name'), sub: tx('swd.scenario.items.0.sub'),
        weeks: '12.0 wks', cost: '$88,000',  badge: null,
        tradeoffs: tx('swd.scenario.items.0.tradeoffs') },
      { id: 's-a',    name: tx('swd.scenario.items.1.name'), sub: tx('swd.scenario.items.1.sub'),
        weeks: '10.0 wks', cost: '$96,000',  badge: 'rec',
        tradeoffs: tx('swd.scenario.items.1.tradeoffs') },
      { id: 's-b',    name: tx('swd.scenario.items.2.name'), sub: tx('swd.scenario.items.2.sub'),
        weeks: '8.5 wks',  cost: '$124,000', badge: 'warn',
        tradeoffs: tx('swd.scenario.items.2.tradeoffs') },
    ],
    perWeekSaved: { a: '$4,000', b: '$13,200' },
    saved: [
      { id: 'sv1', name: 'AI Powered LMS Integration Importer', project: 'AIHR',      weeks: '10 wks',  cost: '$88,000',  best: tx('swd.saved.bestA'),        status: 'live'      },
      { id: 'sv2', name: 'GraphQL public API v2',               project: 'Verova',    weeks: '8.5 wks', cost: '$124,000', best: tx('swd.saved.bestB'),        status: 'live'      },
      { id: 'sv3', name: 'Recurring billing rebuild',           project: 'Helio',     weeks: '14 wks',  cost: '$156,000', best: tx('swd.saved.bestBaseline'), status: 'closed'    },
      { id: 'sv4', name: 'Mobile push notification platform',   project: 'Northpine', weeks: '7 wks',   cost: '$72,000',  best: tx('swd.saved.bestA'),        status: 'discovery' },
    ],
    assumptions: [
      { l: tx('swd.assumptions.items.0.l'), d: tx('swd.assumptions.items.0.d'),
        f: 'speedup = 1 / ((1 minus P) + P / n_effective)' },
      { l: tx('swd.assumptions.items.1.l'), d: tx('swd.assumptions.items.1.d'),
        f: 'n_effective = n × 1 / (1 + α · n(n minus 1) / 30)' },
      { l: tx('swd.assumptions.items.2.l'), d: tx('swd.assumptions.items.2.d'),
        f: 'lost_weeks = Σ ramp_i × prod_loss + onboard_load × ramp_i' },
      { l: tx('swd.assumptions.items.3.l'), d: tx('swd.assumptions.items.3.d'),
        f: 'time × (1 + 0.12 · review + 0.10 · integration + 0.12 · qa + 0.15 · deps)' },
      { l: tx('swd.assumptions.items.4.l'), d: tx('swd.assumptions.items.4.d'),
        f: 'cost_per_week_saved = (cost_new minus cost_base) / (weeks_base minus weeks_new)' },
      { l: tx('swd.assumptions.items.5.l'), d: tx('swd.assumptions.items.5.d'),
        f: '± 12% on baseline · ± 18% on contractor heavy runs' },
    ],
  },

  // ---------- Report provenance (audit-trail event, ported from web/audit/data.js in ScopeWorth.DesignSystem) ----------
  provenance: {
    event: {
      when: 'Today, 10:42',
      connector: 'Jira sync',
      ref: 'EPIC 184',
      title: 'EPIC 184 updated',
      summary: 'Story points 34 → 42 · Sprint moved Q2 → Q3',
      impacts: [
        { l: 'Cost forecast',         v: '+$3,840',         tone: 'warn' },
        { l: 'Delivery risk',         v: 'Medium → High',   tone: 'warn' },
        { l: 'Scope creep',           v: '+8 points',       tone: 'warn' },
      ],
    },
    bullets: [
      '40+ event types tracked across connectors',
      'Before/after state for every mutation',
      'Saved views for finance review, scope changes, recalcs',
      'Hashable event chains clients can verify',
    ],
    savedViews: [
      { id: 'finance',   label: 'Finance review',        meta: 'Cost impacting events only' },
      { id: 'failed',    label: 'Failed syncs',          meta: 'Backoff + retry chains' },
      { id: 'roi',       label: 'ROI changes',           meta: 'Anything moving the score' },
      { id: 'client',    label: 'Client report changes', meta: 'What the client report saw' },
      { id: 'overrides', label: 'Manual overrides',      meta: 'Operator applied corrections' },
      { id: 'health',    label: 'Connector health',      meta: 'Auth, scope, rate limits' },
      { id: 'recalc',    label: 'Recalculations',        meta: 'Cost, margin, forecast reruns' },
    ],
  },

  // ---------- 9 product surfaces (ProductHero preview grid) ----------
  surfaces: [
    { id: 'command',  label: 'Command Center',    value: 'Apr 2026',      tone: 'blue',   note: 'At a glance KPI rail' },
    { id: 'forensic', label: 'Project Forensics', value: '31 items',      tone: 'blue',   note: 'Evidence map · client objectives → outcomes' },
    { id: 'scope',    label: 'Scope Creep',       value: '$8,600',        tone: 'amber',  note: '13 items added after lock' },
    { id: 'invoice',  label: 'Invoice Defense',   value: '$44,800',       tone: 'amber',  note: '+24% vs planned · waterfall' },
    { id: 'ai',       label: 'AI Economics',      value: '+19h',          tone: 'purple', note: 'Illustrative: 26h less, 7h review' },
    { id: 'conf',     label: 'Report Confidence', value: 'Medium · 74%',  tone: 'teal',   note: '5 data health signals' },
    { id: 'builder',  label: 'Report Builder',    value: '9 sections',    tone: 'blue',   note: 'Client-ready export' },
    { id: 'sim',      label: 'Capacity Simulator',value: '10 wks · $96k', tone: 'purple', note: 'Bid the next engagement with math' },
    { id: 'audit',    label: 'Audit Trail',       value: '126 recalcs',   tone: 'teal',   note: 'Every metric · every event' },
  ],

  // ---------- Integration health (Data Sources mini KPI strip) ----------
  integrationHealth: [
    { l: 'Integration health', v: '5/5',        tone: 'teal'   },
    { l: 'Last sync',          v: '2 min ago',  tone: 'teal'   },
    { l: 'Source coverage',    v: '82%',        tone: 'amber'  },
    { l: 'Permission scope',   v: 'Read-only',  tone: 'blue'   },
  ],
};

// ---------- low-level primitives ----------
function Frame({ eyebrow, title, sub, right, children, className = '', innerRef }) {
  return (
    <div ref={innerRef} className={`swd-frame ${className}`}>
      {(eyebrow || title || sub || right) && (
        <div className="swd-frame-head">
          <div>
            {eyebrow && <div className="swd-eyebrow">{eyebrow}</div>}
            {title && <h3 className="swd-frame-title">{title}</h3>}
            {sub && <div className="swd-frame-sub">{sub}</div>}
          </div>
          {right && <div className="swd-frame-right">{right}</div>}
        </div>
      )}
      {children}
    </div>
  );
}

function Tag({ tone = 'blue', children }) {
  return <span className={`swd-tag is-${tone}`}>{children}</span>;
}

// ----- Browser frame -----
// Wraps a section's widgets so they read as a real screen of the ScopeWorth
// dashboard app (app.scopeworth.app/...), mirroring the browser chrome used
// around the product-page demo video. The intent is that what a visitor sees
// on the marketing site is visibly the same surface that lives in the app.
export function BrowserFrame({ url, children, className = '' }) {
  return (
    <div className={`swd-browser ${className}`}>
      <div className="swd-browser-chrome">
        <div className="swd-browser-traffic" aria-hidden="true"><i/><i/><i/></div>
        <div className="swd-browser-address">
          <svg className="swd-browser-lock" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/>
          </svg>
          <span className="swd-browser-url">{url}</span>
        </div>
        <svg className="swd-browser-reload" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21 12a9 9 0 1 1-2.64-6.36M21 3v6h-6"/>
        </svg>
      </div>
      <div className="swd-browser-body">{children}</div>
    </div>
  );
}

// ---------- 1. KPI cards (4-up) ----------
export function KpiCards({ items = SAMPLE.kpis }) {
  const [ref, t, started] = useInViewAnim({ duration: 1400 });
  const ic = (id) => {
    if (id === 'invest')  return <path d="M3 6h18M3 12h18M3 18h12"/>;
    if (id === 'scope')   return <path d="M3 12h18M9 6l-6 6 6 6M15 6l6 6-6 6"/>;
    if (id === 'shipped') return <path d="M3 18h18M5 18l-2-7h18l-2 7M7 11V6a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v5"/>;
    return <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1M9 12a3 3 0 1 0 6 0 3 3 0 0 0-6 0"/>;
  };
  return (
    <div ref={ref} className="swd-kpis">
      {items.map((k, i) => (
        <div key={k.id}
          className={`swd-kpi is-${k.status} swd-anim-rise ${started ? 'is-in' : ''}`}
          style={{ transitionDelay: `${i * 90}ms` }}>
          <div className="swd-kpi-l">
            <span className="swd-kpi-icon" aria-hidden="true">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">{ic(k.id)}</svg>
            </span>
            {k.label}
          </div>
          <div className="swd-kpi-v tabular">{tweenValueString(k.value, t)}</div>
          <div className="swd-kpi-d">{k.delta}</div>
          <div className="swd-kpi-sub">{k.sub}</div>
          <Sparkline id={k.id} t={t}/>
        </div>
      ))}
    </div>
  );
}

function Sparkline({ id, t = 1 }) {
  const series = {
    invest:  [28, 30, 31, 30, 32, 33, 35, 36, 36, 37, 38, 38.4],
    scope:   [0,  0,  0.4, 0.8, 1.6, 2.0, 3.2, 4.0, 4.8, 5.6, 6.4, 6.8],
    shipped: [2,  4,  5,  7,  9, 11, 13, 14, 16, 19, 21, 22],
    ai:      [4,  6,  7,  8, 10, 11, 13, 14, 15, 16, 16.5, 17],
  };
  const colors = { invest: '#FCD34D', scope: '#F87171', shipped: '#5EEAD4', ai: '#C4B5FD' };
  const data = series[id] || [];
  const max = Math.max(...data) || 1;
  const min = Math.min(...data);
  const w = 120, h = 32;
  const step = data.length > 1 ? w / (data.length - 1) : w;
  const d = data.map((v, i) => {
    const x = i * step;
    const y = h - ((v - min) / (max - min || 1)) * (h - 4) - 2;
    return (i === 0 ? `M${x},${y}` : `L${x},${y}`);
  }).join(' ');
  // Reveal mask: a vertical wipe from left → right driven by t.
  const reveal = Math.max(0.0001, t) * w;
  return (
    <svg className="swd-spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <clipPath id={`swd-spark-${id}`}>
          <rect x="0" y="0" width={reveal} height={h}/>
        </clipPath>
      </defs>
      <g clipPath={`url(#swd-spark-${id})`}>
        <path d={`${d} L${w},${h} L0,${h} Z`} fill={colors[id]} fillOpacity="0.12"/>
        <path d={d} fill="none" stroke={colors[id]} strokeWidth="1.6" strokeLinecap="round"/>
      </g>
    </svg>
  );
}

// ---------- 2. Delivery investment flow (compact Sankey) ----------
export function DeliveryInvestmentFlow() {
  const [ref, t] = useInViewAnim({ duration: 1500 });
  const flow = SAMPLE.flow;
  const W = 1100, H = 540;
  const colorMap = { blue: '#60A5FA', teal: '#5EEAD4', amber: '#FCD34D', red: '#F87171', purple: '#C4B5FD' };
  const colInputX = 24;
  const colBucketX = 420;
  const colOutputX = W - 220;
  const inputH = 44, inputGap = 16;
  const inputBlockH = flow.inputs.length * inputH + (flow.inputs.length - 1) * inputGap;
  const inputStartY = (H - inputBlockH) / 2;
  const minBucketH = 54, bucketTopPad = 30, bucketGap = 12;
  const availableH = H - bucketTopPad * 2 - bucketGap * (flow.buckets.length - 1);
  const total = flow.buckets.reduce((s, b) => s + b.pct, 0);
  const heights = (() => {
    const pinned = new Array(flow.buckets.length).fill(false);
    const h = new Array(flow.buckets.length).fill(0);
    for (let iter = 0; iter < flow.buckets.length; iter++) {
      const freeIdx = flow.buckets.map((_, j) => j).filter((j) => !pinned[j]);
      const pinnedSum = flow.buckets.reduce((s, _, j) => s + (pinned[j] ? h[j] : 0), 0);
      const remaining = availableH - pinnedSum;
      const freePctTotal = freeIdx.reduce((s, j) => s + flow.buckets[j].pct, 0) || 1;
      let newPin = false;
      freeIdx.forEach((j) => {
        const raw = (flow.buckets[j].pct / freePctTotal) * remaining;
        if (raw < minBucketH) { h[j] = minBucketH; pinned[j] = true; newPin = true; }
        else { h[j] = raw; }
      });
      if (!newPin) break;
    }
    return h;
  })();
  let cy = bucketTopPad;
  const bucketGeom = flow.buckets.map((b, i) => {
    const hh = heights[i];
    const y = cy; cy += hh + bucketGap;
    return { ...b, y, h: hh };
  });
  const outputY = (H - 130) / 2;
  const outputH = 130;

  const curveIB = (ix, by, bh) => {
    const sx = colInputX + 180, sy = inputStartY + ix * (inputH + inputGap) + inputH / 2;
    const ex = colBucketX, ey = by + bh / 2;
    const cx = sx + (ex - sx) * 0.5;
    return `M${sx},${sy} C${cx},${sy} ${cx},${ey} ${ex},${ey}`;
  };
  const curveBO = (by, bh) => {
    const sx = colBucketX + 240, sy = by + bh / 2;
    const ex = colOutputX, ey = outputY + outputH / 2;
    const cx = sx + (ex - sx) * 0.5;
    return `M${sx},${sy} C${cx},${sy} ${cx},${ey} ${ex},${ey}`;
  };

  return (
    <Frame
      eyebrow={tx('swd.flow.eyebrow')}
      title={tx('swd.flow.title')}
      sub={`${SAMPLE.period} · ${tx('swd.flow.subMeta')}`}
      right={<Tag tone="teal">{tx('swd.live')}</Tag>}
      className="swd-flow-card"
      innerRef={ref}
    >
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" className="swd-flow-svg">
        <defs>
          <linearGradient id="swdOut" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#0F1A2E"/>
            <stop offset="100%" stopColor="#11203A"/>
          </linearGradient>
        </defs>
        {flow.inputs.map((inp, ix) => bucketGeom.map((b, bi) => {
          const c = colorMap[inp.color] || '#60A5FA';
          return (
            <path key={`f-${ix}-${bi}`} d={curveIB(ix, b.y, b.h)} fill="none"
              stroke={c} strokeOpacity={0.15 * t} strokeWidth={Math.max(2, b.h / 60)} strokeLinecap="round"/>
          );
        }))}
        {bucketGeom.map((b, bi) => {
          const c = colorMap[b.color] || '#60A5FA';
          return (
            <path key={`bo-${bi}`} d={curveBO(b.y, b.h)} fill="none"
              stroke={c} strokeOpacity={0.55 * t} strokeWidth={Math.min(32, Math.max(3, b.pct * 0.5))}/>
          );
        })}
        {flow.inputs.map((inp, ix) => {
          const y = inputStartY + ix * (inputH + inputGap);
          return (
            <g key={inp.id} transform={`translate(${colInputX} ${y})`}>
              <rect width="180" height={inputH} rx="8" fill="#0F1A2E" stroke="rgba(148,163,184,0.22)"/>
              <text x="14" y={20} fill="#F1F5F9" fontSize="13" fontWeight="600">{inp.label}</text>
              <text x="14" y={36} fill="#94A3B8" fontSize="11" letterSpacing="0.04em">{inp.count}</text>
            </g>
          );
        })}
        {bucketGeom.map((b) => {
          const c = colorMap[b.color] || '#60A5FA';
          return (
            <g key={b.id} transform={`translate(${colBucketX} ${b.y})`}>
              <rect width="240" height={b.h} rx="8" fill="#11203A" stroke={c} strokeOpacity="0.45"/>
              <rect width="3" height={b.h * t} fill={c}/>
              <text x="18" y="22" fill="#F1F5F9" fontSize="14" fontWeight="600">{b.label}</text>
              <text x="18" y="44" fill={c} fontSize="16" fontWeight="600" letterSpacing="-0.02em" style={{ fontVariantNumeric: 'tabular-nums' }}>{tweenValueString(b.value, t)}</text>
              <text x="226" y="22" fill="#94A3B8" fontSize="11" textAnchor="end" letterSpacing="0.06em">{Math.round(b.pct * t)}%</text>
            </g>
          );
        })}
        <g transform={`translate(${colOutputX} ${outputY})`}>
          <rect width="200" height={outputH} rx="10" fill="url(#swdOut)" stroke="#5EEAD4" strokeOpacity="0.45"/>
          <rect width="200" height="3" rx="10" fill="#5EEAD4" opacity="0.85"/>
          <text x="20" y="36" fill="#5EEAD4" fontSize="11" letterSpacing="0.14em">{tx('swd.flow.outputHeading')}</text>
          <text x="20" y="64" fill="#F1F5F9" fontSize="16" fontWeight="600">{flow.output.label}</text>
          <text x="20" y="96" fill="#5EEAD4" fontSize="24" fontWeight="700" letterSpacing="-0.02em" style={{ fontVariantNumeric: 'tabular-nums' }}>{tweenValueString(flow.output.value, t)}</text>
          <text x="20" y="116" fill="#94A3B8" fontSize="10" letterSpacing="0.10em">{flow.output.sub.toUpperCase()}</text>
        </g>
      </svg>
      <div className="swd-flow-legend">
        <span><i style={{ background: '#60A5FA' }}/>{tx('swd.flow.legendBuild')}</span>
        <span><i style={{ background: '#C4B5FD' }}/>{tx('swd.flow.legendCoord')}</span>
        <span><i style={{ background: '#F87171' }}/>{tx('swd.flow.legendRework')}</span>
        <span><i style={{ background: '#FCD34D' }}/>{tx('swd.flow.legendOther')}</span>
        <span><i style={{ background: '#5EEAD4' }}/>{tx('swd.flow.legendOutput')}</span>
      </div>
    </Frame>
  );
}

// ---------- 3. Cost driver breakdown ----------
export function CostDriverBreakdown() {
  const [ref, t, started] = useInViewAnim({ duration: 1200 });
  const colorMap = { blue: '#60A5FA', teal: '#5EEAD4', amber: '#FCD34D', red: '#F87171', purple: '#C4B5FD' };
  return (
    <Frame eyebrow={tx('swd.costDriver.eyebrow')} title={`${SAMPLE.period} · ${tx('swd.costDriver.titleSuffix')}`} innerRef={ref}>
      <div className="swd-grid-5">
        {SAMPLE.flow.buckets.map((b, i) => (
          <div key={b.id}
            className={`swd-driver swd-anim-rise ${started ? 'is-in' : ''}`}
            style={{ transitionDelay: `${i * 80}ms` }}>
            <div className="swd-driver-h">
              <span className="swd-driver-l">{b.label}</span>
              <Tag tone={b.color}>{Math.round(b.pct * t)}%</Tag>
            </div>
            <div className="swd-driver-v tabular">{tweenValueString(b.value, t)}</div>
            <div className="swd-driver-bar"><span style={{ width: `${b.pct * t}%`, background: colorMap[b.color] }}/></div>
          </div>
        ))}
      </div>
    </Frame>
  );
}

// ---------- 4. What changed this month (insights rail) ----------
export function WhatChangedInsights() {
  const [ref, , started] = useInViewAnim();
  const dot = { risk: '#F87171', warn: '#FCD34D', ai: '#C4B5FD', ok: '#5EEAD4' };
  return (
    <Frame eyebrow={tx('swd.whatChanged.eyebrow')} title={tx('swd.whatChanged.title')} sub={`${SAMPLE.period}`} innerRef={ref}>
      <div className="swd-insights">
        {SAMPLE.insights.map((i, k) => (
          <div key={k}
            className={`swd-insight is-${i.kind} swd-anim-slide ${started ? 'is-in' : ''}`}
            style={{ transitionDelay: `${k * 120}ms` }}>
            <span className="swd-insight-dot" style={{ background: dot[i.kind] }}/>
            <div className="swd-insight-body">
              <div className="swd-insight-t">{i.body}</div>
              <div className="swd-insight-m">{i.meta}</div>
            </div>
          </div>
        ))}
      </div>
    </Frame>
  );
}

// ---------- 5. Delivery proof map ----------
export function DeliveryProofMap() {
  const [ref, , started] = useInViewAnim();
  const p = SAMPLE.proof;
  const statusLabel = (s) => s === 'ok' ? tx('swd.proof.stShipped') : s === 'warn' ? tx('swd.proof.stMidCycle') : tx('swd.proof.stAddedScope');
  // Stagger across all nodes in a left-to-right wave; columns offset by ~80ms.
  const node = (idx, col) => ({
    className: `swd-proof-node swd-anim-slide ${started ? 'is-in' : ''}`,
    style: { transitionDelay: `${col * 80 + idx * 60}ms` },
  });
  return (
    <Frame eyebrow={tx('swd.proof.eyebrow')} title={tx('swd.proof.title')} sub={tx('swd.proof.sub')} innerRef={ref}>
      <div className="swd-proof">
        <div className="swd-proof-col">
          <div className="swd-proof-h">{tx('swd.proof.colObjective')}</div>
          <div {...node(0, 0)} className={`${node(0, 0).className} is-selected`}>
            <div className="swd-proof-t">{p.objective.t}</div>
            <div className="swd-proof-m">{p.objective.meta}</div>
            <span className="swd-proof-status ok">{tx('swd.proof.active')}</span>
          </div>
          <div className="swd-eyebrow swd-mt">{tx('swd.proof.scopeBaseline')}</div>
          <div {...node(1, 0)} className={`${node(1, 0).className} is-dashed`}>
            <div className="swd-proof-t">{tx('swd.proof.lockedPrefix')} {SAMPLE.scopeLock}</div>
            <div className="swd-proof-m">{tx('swd.proof.committed')}</div>
          </div>
        </div>
        <div className="swd-proof-col">
          <div className="swd-proof-h">{tx('swd.proof.colEpics')}</div>
          {p.epics.map((e, k) => (
            <div key={e.id} {...node(k, 1)}>
              <div className="swd-proof-t">{e.t}</div>
              <div className="swd-proof-m">{e.meta}</div>
              {e.note && <div className="swd-proof-note">{e.note}</div>}
              <span className={`swd-proof-status ${e.status}`}>{statusLabel(e.status)}</span>
            </div>
          ))}
        </div>
        <div className="swd-proof-col">
          <div className="swd-proof-h">{tx('swd.proof.colItems')}</div>
          {p.items.map((it, k) => (
            <div key={k} {...node(k, 2)}>
              <div className="swd-proof-t">{it.t}</div>
              <div className="swd-proof-m">{it.meta}</div>
              <span className={`swd-proof-status ${it.status}`}>{it.status === 'ok' ? tx('swd.proof.stShipped') : it.status === 'warn' ? tx('swd.proof.stAcChanged') : tx('swd.proof.stAdded')}</span>
            </div>
          ))}
        </div>
        <div className="swd-proof-col">
          <div className="swd-proof-h">{tx('swd.proof.colOutcomes')}</div>
          {p.outcomes.map((o, k) => (
            <div key={k} {...node(k, 3)}>
              <div className="swd-proof-t">{o.t}</div>
              <div className="swd-proof-m">{o.meta}</div>
              <span className="swd-proof-status ok">{tx('swd.proof.measured')}</span>
            </div>
          ))}
        </div>
      </div>
    </Frame>
  );
}

// ---------- 6. Forensic timeline ----------
export function ForensicTimeline() {
  const [ref, t, started] = useInViewAnim({ duration: 1600 });
  return (
    <Frame eyebrow={tx('swd.timeline.eyebrow')} title={`${tx('swd.timeline.titlePrefix')} · ${SAMPLE.period}`} sub={tx('swd.timeline.sub')} innerRef={ref}>
      <div className={`swd-tl ${started ? 'is-in' : ''}`} style={{ '--swd-tl-fill': `${t * 100}%` }}>
        {SAMPLE.timeline.map((ev, i) => (
          <div key={i}
            className={`swd-tl-row is-${ev.kind} swd-anim-slide ${started ? 'is-in' : ''}`}
            style={{ transitionDelay: `${i * 110}ms` }}>
            <div className="swd-tl-date">{ev.date}</div>
            <div className="swd-tl-body">
              <div className="swd-tl-t">{ev.title}</div>
              <div className="swd-tl-d">{ev.desc}</div>
            </div>
            <div className="swd-tl-right">
              <span className="swd-tl-tag">{ev.tag}</span>
              {ev.cost && <span className="swd-tl-cost">{tweenValueString(ev.cost, t)}</span>}
            </div>
          </div>
        ))}
      </div>
    </Frame>
  );
}

// ---------- 7. Client question (Q&A pair) ----------
export function ClientQuestionCard() {
  const [ref, , started] = useInViewAnim();
  return (
    <Frame eyebrow={tx('swd.clientQuestion.eyebrow')} title={tx('swd.clientQuestion.title')} sub={tx('swd.clientQuestion.sub')} innerRef={ref}>
      <div className="swd-qa">
        <div className={`swd-qa-q swd-anim-slide ${started ? 'is-in' : ''}`}>
          <div className="swd-eyebrow swd-amber">{tx('swd.clientQuestion.fromClient')}</div>
          <div className="swd-qa-text">{tx('swd.clientQuestion.question')}</div>
        </div>
        <div className={`swd-qa-a swd-anim-slide ${started ? 'is-in' : ''}`}
          style={{ transitionDelay: '180ms' }}>
          <div className="swd-eyebrow swd-teal">{tx('swd.clientQuestion.answerLabel')}</div>
          <div className="swd-qa-text" dangerouslySetInnerHTML={{ __html: tx('swd.clientQuestion.answerHtml') }} />
          <div className="swd-qa-actions">
            <span className="swd-qa-chip is-amber">{tx('swd.clientQuestion.chipAdded')}</span>
            <span className="swd-qa-chip is-red">{tx('swd.clientQuestion.chipReopened')}</span>
            <span className="swd-qa-chip is-red">{tx('swd.clientQuestion.chipReview')}</span>
            {/* Amber, not violet: DESIGN.md reserves violet for AI insight, and
                coordination is already an amber "watch" tile in the strip above. */}
            <span className="swd-qa-chip is-amber">{tx('swd.clientQuestion.chipCoordination')}</span>
          </div>
        </div>
      </div>
    </Frame>
  );
}

// ---------- 8. Cost drivers table (evidence behind every line) ----------
export function CostDriversTable() {
  const [ref, t, started] = useInViewAnim({ duration: 1300 });
  return (
    <Frame eyebrow={tx('swd.costTable.eyebrow')} title={tx('swd.costTable.title')} sub={tx('swd.costTable.sub')} innerRef={ref}>
      <div className="swd-tbl-wrap">
      <table className="swd-tbl">
        <thead>
          <tr>
            <th>{tx('swd.costTable.thDriver')}</th>
            <th>{tx('swd.costTable.thEvidence')}</th>
            <th className="num">{tx('swd.costTable.thImpact')}</th>
            <th>{tx('swd.costTable.thExplanation')}</th>
          </tr>
        </thead>
        <tbody>
          {SAMPLE.invoiceTable.map((r, i) => (
            <tr key={i}
              className={`swd-anim-slide ${started ? 'is-in' : ''}`}
              style={{ transitionDelay: `${i * 90}ms` }}>
              <td><strong>{r.driver}</strong></td>
              <td className="muted">{r.evidence}</td>
              <td className="num"><span className="swd-tbl-num">{tweenValueString(r.impact, t)}</span></td>
              <td className="muted">{r.explain}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </Frame>
  );
}

// ---------- 9. Invoice waterfall ----------
// Side-by-side bar chart: every bar anchors at the chart baseline (y=innerH)
// and grows upward to its value. Base + total are full-height anchors; the
// "add" bars show their individual $-impact next to them. Hovering a bar
// reveals the evidence behind it as a floating tip.
const WATERFALL_DETAIL = {
  plan: { eyebrow: tx('swd.waterfall.detail.plan.eyebrow'), title: tx('swd.waterfall.detail.plan.title'), evidence: tx('swd.waterfall.detail.plan.evidence'), action: tx('swd.waterfall.detail.plan.action') },
  add:  { eyebrow: tx('swd.waterfall.detail.add.eyebrow'),  title: tx('swd.waterfall.detail.add.title'),  evidence: tx('swd.waterfall.detail.add.evidence'),  action: tx('swd.waterfall.detail.add.action') },
  rw:   { eyebrow: tx('swd.waterfall.detail.rw.eyebrow'),   title: tx('swd.waterfall.detail.rw.title'),   evidence: tx('swd.waterfall.detail.rw.evidence'),   action: tx('swd.waterfall.detail.rw.action') },
  rev:  { eyebrow: tx('swd.waterfall.detail.rev.eyebrow'),  title: tx('swd.waterfall.detail.rev.title'),  evidence: tx('swd.waterfall.detail.rev.evidence'),  action: tx('swd.waterfall.detail.rev.action') },
  mtg:  { eyebrow: tx('swd.waterfall.detail.mtg.eyebrow'),  title: tx('swd.waterfall.detail.mtg.title'),  evidence: tx('swd.waterfall.detail.mtg.evidence'),  action: tx('swd.waterfall.detail.mtg.action') },
  tot:  { eyebrow: tx('swd.waterfall.detail.tot.eyebrow'),  title: tx('swd.waterfall.detail.tot.title'),  evidence: tx('swd.waterfall.detail.tot.evidence'),  action: tx('swd.waterfall.detail.tot.action') },
};
export function InvoiceWaterfall() {
  const [ref, t] = useInViewAnim({ duration: 1400 });
  const [hot, setHot] = React.useState(null);
  const rows = SAMPLE.waterfall;
  const W = 880, H = 280, padX = 24, padTop = 22, padBot = 56;
  const innerH = H - padTop - padBot;
  const max = Math.max(...rows.map(r => r.value));
  const yScale = (v) => innerH - (v / max) * innerH;
  const colW = (W - padX * 2) / rows.length - 18;
  const colors = { base: '#60A5FA', add: '#FCD34D', total: '#5EEAD4' };
  const geom = rows.map((r, i) => ({
    ...r,
    x: padX + i * (colW + 18),
    yTop: yScale(r.value),
    color: colors[r.kind] || '#60A5FA',
    w: colW,
  }));
  const tip = hot ? geom.find((g) => g.id === hot) : null;
  const tipMeta = tip ? WATERFALL_DETAIL[tip.id] : null;
  // Tip is positioned in percentage of the SVG viewBox so it sticks to its bar
  // regardless of how the SVG scales to fit the container.
  const tipLeftPct = tip ? ((tip.x + tip.w / 2) / W) * 100 : 0;
  const tipTopPct  = tip ? ((padTop + (innerH - (innerH - tip.yTop) * t)) / H) * 100 : 0;

  return (
    <Frame eyebrow={tx('swd.waterfall.eyebrow')} title={tx('swd.waterfall.title')} sub={tx('swd.waterfall.sub')} innerRef={ref}>
      <div className="swd-wf-hint">{tx('swd.waterfall.hint')}</div>
      {/* Scrolls sideways below 760px, so it needs to be a focusable, named
          region — a keyboard or screen-reader user otherwise cannot reach the
          bars that sit past the frame edge. */}
      <div
        className="swd-waterfall"
        tabIndex={0}
        role="region"
        aria-label={tx('swd.waterfall.title')}
      >
        <div className="swd-waterfall-inner">
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet">
          {[0, 0.25, 0.5, 0.75, 1].map((p, i) => (
            <line key={i} x1={padX} x2={W - padX}
              y1={padTop + innerH * p} y2={padTop + innerH * p}
              stroke="rgba(148,163,184,0.10)" strokeDasharray="3 6"/>
          ))}
          {geom.map((g) => {
            // Each bar grows from the chart baseline (innerH) up to yTop.
            const fullH = innerH - g.yTop;
            const animH = fullH * t;
            const barY = innerH - animH;
            const animVal = Math.round(g.value * t);
            const isHot = hot === g.id;
            const dim = hot && !isHot;
            const cls = `swd-wf-bar ${isHot ? 'is-hot' : ''} ${dim ? 'is-dim' : ''}`;
            return (
              <g key={g.id} transform={`translate(0 ${padTop})`}
                 className={cls} style={{ color: g.color }}
                 onMouseEnter={() => setHot(g.id)}
                 onMouseLeave={() => setHot(null)}
                 onFocus={() => setHot(g.id)}
                 onBlur={() => setHot(null)}
                 tabIndex={0}>
                <rect x={g.x - 4} y={barY - 4}
                  width={g.w + 8} height={animH + 8} rx="8"
                  fill={isHot ? g.color : 'transparent'}
                  fillOpacity={isHot ? 0.10 : 0}/>
                <rect x={g.x} y={barY} width={g.w}
                  height={animH} rx="6"
                  fill={g.color}
                  fillOpacity={g.kind === 'total' ? 0.95 : g.kind === 'base' ? 0.85 : 0.78}/>
                <text x={g.x + g.w/2} y={barY - 8} textAnchor="middle"
                  fill="#F1F5F9" fontSize="13" fontWeight="600" opacity={t}
                  style={{ fontVariantNumeric: 'tabular-nums' }}>${animVal.toLocaleString('en-GB')}</text>
                <text x={g.x + g.w/2} y={innerH + 22} textAnchor="middle"
                  fill="#94A3B8" fontSize="11">{g.label}</text>
              </g>
            );
          })}
        </svg>
        {tip && tipMeta && (
          <div className={`swd-wf-tip is-in`}
               style={{ left: `${tipLeftPct}%`, top: `${tipTopPct}%` }}
               role="tooltip">
            <div className="swd-wf-tip-eyebrow">{tipMeta.eyebrow}</div>
            <div className="swd-wf-tip-title">{tipMeta.title}</div>
            <div className={`swd-wf-tip-impact is-${tip.kind}`}>${tip.value.toLocaleString('en-GB')}</div>
            <div className="swd-wf-tip-evidence">{tipMeta.evidence}</div>
            <div className="swd-wf-tip-action">{tipMeta.action}</div>
          </div>
        )}
        </div>
      </div>
    </Frame>
  );
}

// ---------- 10. AI cycle time (dual line) ----------
export function AICycleTime() {
  const [ref, t] = useInViewAnim({ duration: 1600 });
  const a = SAMPLE.ai.cycleAi, b = SAMPLE.ai.cycleNonAi;
  const W = 720, H = 240, padL = 48, padR = 16, padT = 16, padB = 36;
  const innerW = W - padL - padR, innerH = H - padT - padB;
  const max = Math.max(...a, ...b) * 1.1;
  const xStep = innerW / (a.length - 1);
  const toPath = (arr) => arr.map((v, i) => {
    const x = padL + i * xStep;
    const y = padT + innerH - (v / max) * innerH;
    return (i === 0 ? `M${x},${y}` : `L${x},${y}`);
  }).join(' ');
  const pathA = toPath(a), pathB = toPath(b);

  // Approximate path lengths for stroke-dash reveal (good enough for an
  // 800-px-wide chart — exact getTotalLength would force a layout pass).
  const lineLen = (arr) => arr.reduce((s, v, i) => {
    if (i === 0) return 0;
    const x = xStep, y = ((arr[i-1] - v) / max) * innerH;
    return s + Math.hypot(x, y);
  }, 0);
  const lenA = lineLen(a) || 1, lenB = lineLen(b) || 1;
  const endXA = padL + (a.length - 1) * xStep;
  const endYA = padT + innerH - (a[a.length-1] / max) * innerH;
  const endXB = padL + (b.length - 1) * xStep;
  const endYB = padT + innerH - (b[b.length-1] / max) * innerH;

  return (
    <Frame eyebrow={tx('swd.aiCycle.eyebrow')} title={tx('swd.aiCycle.title')} sub={tx('swd.aiCycle.sub')} innerRef={ref}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" className="swd-chart">
        <defs>
          <linearGradient id="swdAi" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#C4B5FD" stopOpacity="0.30"/>
            <stop offset="100%" stopColor="#C4B5FD" stopOpacity="0"/>
          </linearGradient>
        </defs>
        {[0, 0.25, 0.5, 0.75, 1].map((p, i) => (
          <g key={i}>
            <line x1={padL} x2={W - padR} y1={padT + innerH * p} y2={padT + innerH * p} stroke="rgba(148,163,184,0.10)"/>
            <text x={padL - 8} y={padT + innerH * p + 4} fontSize="10" fill="#64748B" textAnchor="end">{(max - p * max).toFixed(1)}h</text>
          </g>
        ))}
        {a.map((_, i) => (i % 2 === 0 ? (
          <text key={i} x={padL + i * xStep} y={H - 12} fontSize="10" fill="#64748B" textAnchor="middle">D{i + 1}</text>
        ) : null))}
        <path d={`${pathA} L${padL + (a.length-1)*xStep},${padT + innerH} L${padL},${padT + innerH} Z`}
          fill="url(#swdAi)" opacity={t}/>
        <path d={pathB} fill="none" stroke="#94A3B8" strokeWidth="1.6"
          strokeDasharray={lenB} strokeDashoffset={lenB * (1 - t)} strokeLinecap="round"/>
        <path d={pathA} fill="none" stroke="#C4B5FD" strokeWidth="2.4"
          strokeDasharray={lenA} strokeDashoffset={lenA * (1 - t)} strokeLinecap="round"/>
        <circle cx={endXA} cy={endYA} r="3.5" fill="#C4B5FD" opacity={t}/>
        <circle cx={endXB} cy={endYB} r="3"   fill="#94A3B8" opacity={t}/>
        <g transform={`translate(${padL} ${padT - 6})`}>
          <circle cx="4" cy="4" r="3.5" fill="#C4B5FD"/>
          <text x="14" y="7" fontSize="11" fill="#F1F5F9">{tx('swd.aiCycle.aiAssisted')}</text>
          <circle cx="120" cy="4" r="3" fill="#94A3B8"/>
          <text x="130" y="7" fontSize="11" fill="#94A3B8">{tx('swd.aiCycle.baseline')}</text>
        </g>
      </svg>
      <div className="swd-note">
        <strong>{tx('swd.aiCycle.trendLabel')}</strong>{tx('swd.aiCycle.trend')}
      </div>
    </Frame>
  );
}

// ---------- 11. Review overhead bars ----------
export function ReviewOverheadBars() {
  const [ref, t] = useInViewAnim({ duration: 1100 });
  const data = SAMPLE.ai.review;
  const W = 720, H = 240, padL = 110, padR = 28, padT = 12, padB = 28;
  const innerW = W - padL - padR, innerH = H - padT - padB;
  const max = Math.max(...data.flatMap(d => [d.ai, d.base])) * 1.1;
  const rowH = innerH / data.length;
  return (
    <Frame eyebrow={tx('swd.reviewOverhead.eyebrow')} title={tx('swd.reviewOverhead.title')} sub={tx('swd.reviewOverhead.sub')} innerRef={ref}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" className="swd-chart">
        {data.map((d, i) => {
          const yBase = padT + i * rowH + 6;
          const aiW = (d.ai / max) * innerW * t;
          const baseW = (d.base / max) * innerW * t;
          const barH = (rowH - 14) / 2;
          return (
            <g key={d.l}>
              <text x={padL - 12} y={yBase + rowH/2 + 2} textAnchor="end" fontSize="12" fill="#CBD5E1">{tx('swd.reviewOverhead.rows.' + d.l)}</text>
              <rect x={padL} y={yBase} width={aiW} height={barH} rx="3" fill="#C4B5FD" fillOpacity="0.85"/>
              <text x={padL + aiW + 6} y={yBase + barH - 2} fontSize="10.5" fill="#C4B5FD" opacity={t}>{Math.round(d.ai * t)}%</text>
              <rect x={padL} y={yBase + barH + 2} width={baseW} height={barH} rx="3" fill="#94A3B8" fillOpacity="0.55"/>
              <text x={padL + baseW + 6} y={yBase + barH * 2 + 1} fontSize="10.5" fill="#94A3B8" opacity={t}>{Math.round(d.base * t)}%</text>
            </g>
          );
        })}
      </svg>
      <div className="swd-note swd-note-amber">
        <strong>{tx('swd.reviewOverhead.watchLabel')}</strong>{tx('swd.reviewOverhead.watch')}
      </div>
    </Frame>
  );
}

// ---------- 12. Confidence gauge ----------
export function ConfidenceGaugeWidget() {
  const [ref, t, started] = useInViewAnim({ duration: 1500 });
  const { score, label, health } = SAMPLE.confidence;
  const colorFor = (s) => s === 'ok' ? '#5EEAD4' : s === 'warn' ? '#FCD34D' : '#F87171';
  const R = 80, C = Math.PI * R;
  const animScore = score * t;
  const offset = C * (1 - animScore / 100);
  return (
    <Frame eyebrow={tx('swd.confidence.eyebrow')} title={tx('swd.confidence.title')} sub={tx('swd.confidence.sub')} innerRef={ref}>
      <div className="swd-gauge-wrap">
        <div className="swd-gauge">
          <svg viewBox="0 0 200 120" preserveAspectRatio="xMidYMid meet">
            <path d="M20 100 A 80 80 0 0 1 180 100" fill="none" stroke="rgba(148,163,184,0.18)" strokeWidth="14" strokeLinecap="round"/>
            <path d="M20 100 A 80 80 0 0 1 180 100" fill="none" stroke="#5EEAD4" strokeWidth="14" strokeLinecap="round"
              strokeDasharray={C} strokeDashoffset={offset}/>
          </svg>
          <div className="swd-gauge-v tabular">{Math.round(animScore)}%</div>
          <div className="swd-gauge-l">{label}</div>
        </div>
        <div className="swd-health">
          {health.map((h, i) => (
            <div key={i}
              className={`swd-health-row swd-anim-slide ${started ? 'is-in' : ''}`}
              style={{ transitionDelay: `${i * 110}ms` }}>
              <div>
                <div className="swd-health-t">{h.t}</div>
                <div className="swd-health-m">{h.note}</div>
                <div className="swd-health-bar"><span style={{ width: `${h.pct * t}%`, background: colorFor(h.status) }}/></div>
              </div>
              <div className="swd-health-pct tabular">
                {Math.round(h.pct * t)}%
                <Tag tone={h.status === 'ok' ? 'teal' : h.status === 'warn' ? 'amber' : 'red'}>
                  {h.status === 'ok' ? tx('swd.confidence.tagHealthy') : h.status === 'warn' ? tx('swd.confidence.tagImprove') : tx('swd.confidence.tagWeak')}
                </Tag>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Frame>
  );
}

// ---------- 13. Connected tools ----------
export function ConnectedTools() {
  const [ref, , started] = useInViewAnim();
  return (
    <Frame eyebrow={tx('swd.connected.eyebrow')} title={tx('swd.connected.title')} sub={tx('swd.connected.sub')} innerRef={ref}>
      <div className="swd-int-grid">
        {SAMPLE.integrations.map((i, k) => (
          <div key={i.id}
            className={`swd-int swd-anim-rise ${started ? 'is-in' : ''}`}
            style={{ transitionDelay: `${k * 80}ms` }}>
            <div className="swd-int-h">
              <div className="swd-int-logo"><IntegrationLogo name={i.name} size={22} onDark/></div>
              <div>
                <div className="swd-int-n">{i.name}</div>
                <div className="swd-int-k">{i.kind}</div>
              </div>
            </div>
            <div className={`swd-int-s is-${i.status}`}>
              <span className="swd-int-dot"/>
              {i.evidenceFlag === 'example_connected' ? tx('swd.connected.exampleConnected') : tx('swd.connected.limitedSetup')}
            </div>
            <div className="swd-int-m">{i.meta}</div>
          </div>
        ))}
      </div>
      <div className="swd-int-note">
        <strong>{tx('swd.connected.sampleLabel')}</strong>{tx('swd.connected.sampleNote')}
        <br />
        <strong>{tx('swd.connected.noteLabel')}</strong>{tx('swd.connected.note')}
      </div>
    </Frame>
  );
}

// ============================================================
// Section wrappers — composed widgets for marketing pages
// ============================================================

// Home: just below the hero. Anatomy of a ScopeWorth report.
export function ReportAnatomySection() {
  return (
    <section className="sw-section swd-section">
      <div className="sw-container">
        <div className="sw-section-head sw-section-head-center">
          <div className="sw-eyebrow swd-teal">{tx('widgets.reportAnatomy.eyebrow')}</div>
          <h2 className="sw-h2 swd-h2">{tx('widgets.reportAnatomy.title')}</h2>
          <p className="sw-lead">{tx('widgets.reportAnatomy.lead')}</p>
        </div>
        <BrowserFrame url="app.scopeworth.app/farhaven/command">
          <KpiCards/>
          <DeliveryInvestmentFlow/>
          <div className="swd-split-2">
            <CostDriverBreakdown/>
            <WhatChangedInsights/>
          </div>
        </BrowserFrame>
      </div>
    </section>
  );
}

// Home: invoice defense story (client question + waterfall)
export function InvoiceDefenseSection() {
  return (
    <section className="sw-section swd-section">
      <div className="sw-container">
        <div className="sw-section-head sw-section-head-center">
          <div className="sw-eyebrow swd-amber">{tx('widgets.invoiceDefense.eyebrow')}</div>
          <h2 className="sw-h2 swd-h2">{tx('widgets.invoiceDefense.title')}</h2>
        </div>
        <BrowserFrame url="app.scopeworth.app/farhaven/invoice">
          <ClientQuestionCard/>
          <InvoiceWaterfall/>
          <CostDriversTable/>
        </BrowserFrame>
      </div>
    </section>
  );
}

// Product: forensic story (deep dive exhibit)
// The Project Forensics deep dive, in the investigate-roi landing's Exhibit C
// dress: the argument for forensics as intro copy, then a mock player in the
// product hero's full uniform (chrome bar, stage, transport) running a muted
// 10s 1080p loop cut from 0:56 of the deep dive. Every control opens the real
// video in the demo modal (`forensics` entry).
//
// The transport numbers are derived from the video itself so the mock cannot
// drift from what it launches.
const DEEP_DIVE_RUNTIME_S = 522;    // 8:42 — matches widgets.forensics.deepDive.modalMeta
const DEEP_DIVE_PREVIEW_AT_S = 56;  // where the looping cut sits in the deep dive
const DEEP_DIVE_DIR = '/marketing/videos/investigation-canvas';
const DEEP_DIVE_LOOP = `${DEEP_DIVE_DIR}/forensics-preview-loop.mp4`;
const DEEP_DIVE_POSTER = `${DEEP_DIVE_DIR}/forensics-preview-poster.jpg`;
const DEEP_DIVE_POSTER_960 = `${DEEP_DIVE_DIR}/forensics-preview-poster-960.jpg`;

function clock(seconds, pad = false) {
  const m = Math.floor(seconds / 60);
  const s = String(Math.round(seconds % 60)).padStart(2, '0');
  return `${pad ? String(m).padStart(2, '0') : m}:${s}`;
}

export function ForensicsDeepDiveCard() {
  const videoRef = React.useRef(null);
  // Reduced motion gets the matching still instead of the animated loop.
  const [still, setStill] = React.useState(() =>
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  );
  React.useEffect(() => {
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setStill(motion.matches);
    motion.addEventListener?.('change', onChange);
    return () => motion.removeEventListener?.('change', onChange);
  }, []);

  // Playback waits for the window `load` event so the loop never competes with
  // the page's LCP; `preload="none"` means nothing is fetched until then.
  React.useEffect(() => {
    if (still) return undefined;
    const start = () => {
      const v = videoRef.current;
      if (!v) return;
      v.load();
      v.play().catch(() => { /* autoplay refused — the poster stays */ });
    };
    if (document.readyState === 'complete') { start(); return undefined; }
    window.addEventListener('load', start, { once: true });
    return () => window.removeEventListener('load', start);
  }, [still]);

  const copy = tx('widgets.forensics.deepDive');
  const open = () => window.openDemoTour?.('product-forensics', 'forensics');
  const progress = `${(DEEP_DIVE_PREVIEW_AT_S / DEEP_DIVE_RUNTIME_S) * 100}%`;

  return (
    <div className="swd-deepdive" role="region" aria-label={copy?.regionLabel}>
      <button type="button" className="sw-btn sw-btn-primary swd-deepdive-cta" onClick={open}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5.5v13a1 1 0 0 0 1.55.83l10-6.5a1 1 0 0 0 0-1.66l-10-6.5A1 1 0 0 0 8 5.5Z"/></svg>
        <span className="sw-btn-label">{copy?.cta}</span>
      </button>

      <div className="sw-video-frame swd-deepdive-frame">
        <div className="sw-video-chrome">
          <div className="sw-video-traffic" aria-hidden="true"><i/><i/><i/></div>
          <div className="sw-video-title"><strong>ScopeWorth</strong> · {copy?.modalTitle}</div>
          <div className="sw-video-stamp">{clock(DEEP_DIVE_RUNTIME_S)}</div>
        </div>
        <div className="sw-video-stage">
          {/* Decorative backdrop: the play control below is the accessible
              control, and clicking the loop anywhere opens the same video. */}
          <div className="sw-video-preview" aria-hidden="true" onClick={open}>
            {still ? (
              <img
                className="sw-video-preview-media"
                src={DEEP_DIVE_POSTER}
                srcSet={`${DEEP_DIVE_POSTER_960} 960w, ${DEEP_DIVE_POSTER} 1920w`}
                sizes="min(92vw, 980px)"
                width="1920"
                height="1080"
                alt=""
                decoding="async"
              />
            ) : (
              <video
                ref={videoRef}
                className="sw-video-preview-media"
                muted
                loop
                playsInline
                preload="none"
                poster={DEEP_DIVE_POSTER}
                src={DEEP_DIVE_LOOP}
                tabIndex={-1}
              />
            )}
            <div className="sw-video-preview-veil"></div>
          </div>

          <button type="button" className="sw-video-play" aria-label={copy?.play} onClick={open}>
            <span className="sw-video-play-ring" aria-hidden="true"/>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5.5v13a1 1 0 0 0 1.55.83l10-6.5a1 1 0 0 0 0-1.66l-10-6.5A1 1 0 0 0 8 5.5Z"/></svg>
          </button>

          {/* Runtime chip. No pulsing dot: this is a recording, not a live feed. */}
          <div className="sw-video-chips">
            <span className="sw-video-chip">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
              {copy?.live}
            </span>
          </div>
        </div>

        {/* A transport, not a player: it reads the deep dive's own timings.
            Only the play control is interactive; the rest is decorative so
            keyboard users do not meet a row of identical stops. */}
        <div className="sw-video-controls">
          <button type="button" className="sw-video-ctrl" aria-label={copy?.play} onClick={open}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>
          </button>
          <div className="sw-video-time">{clock(DEEP_DIVE_PREVIEW_AT_S, true)}</div>
          <div className="sw-video-scrub" aria-hidden="true">
            <span className="sw-video-scrub-fill" style={{ width: progress }}/>
            <span className="sw-video-scrub-knob" style={{ left: progress }}/>
          </div>
          <div className="sw-video-time">{clock(DEEP_DIVE_RUNTIME_S, true)}</div>
          <span className="sw-video-ctrl" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 5 6 9H2v6h4l5 4zM15.5 8.5a5 5 0 0 1 0 7M19 5a9 9 0 0 1 0 14"/></svg>
          </span>
          <span className="sw-video-ctrl" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/></svg>
          </span>
        </div>
      </div>
      <p className="sw-demo-modal-meta">{tx('demoModal.disclosure')}</p>
    </div>
  );
}

// The evidence spine: a static chain-of-evidence vignette beside the deep
// dive, drawn from the dashboard's forensics case board (ScopeWorth.Dashboard
// forensics-graph.jsx / forensics-styles.css / forensics-data.js). Same node
// anatomy (accent-tinted body on the card surface, confidence ring, 24×24
// stroke glyphs, labels under story nodes only), same edge taxonomy (solid
// traced links, dashed amber cost flags, dashed red untraced), and the
// canonical Farhaven / Portcall Ops nodes: the objective feeding the epic
// thread, the $8,600 scope expansion, releases v2.7/v2.8, the +21% operator
// adoption outcome, and the untraced PR. Decorative only — the real, live
// board is what the deep dive video walks through.
function SpineGlyph({ type }) {
  const g = (children) => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <g stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round">{children}</g>
    </svg>
  );
  switch (type) {
    case 'objective': return g(<><circle cx="12" cy="12" r="7"/><circle cx="12" cy="12" r="2.4" fill="currentColor" stroke="none"/></>);
    case 'epic':      return g(<><path d="M12 4 4 8.2l8 4.2 8-4.2z"/><path d="M4 12.2 12 16.4l8-4.2"/></>);
    case 'item':      return g(<><rect x="5" y="6" width="14" height="12" rx="2"/><path d="M9 10h6M9 14h4"/></>);
    case 'pr':        return g(<><circle cx="8" cy="7" r="2"/><circle cx="8" cy="17" r="2"/><circle cx="16" cy="17" r="2"/><path d="M8 9v6M16 15v-3a2 2 0 0 0-2-2h-3"/></>);
    case 'release':   return g(<path d="M14 5h5v5M19 5l-7.5 7.5M19 13v4a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h4"/>);
    case 'meeting':   return g(<><circle cx="9.5" cy="9" r="3"/><path d="M4.5 18a5 5 0 0 1 10 0"/><circle cx="16.5" cy="10" r="2.2"/></>);
    case 'outcome':   return g(<path d="M5 12.5l4.2 4L19 7"/>);
    case 'cost':      return (
      <svg viewBox="0 0 24 24" aria-hidden="true"><text x="12" y="16.5" fontSize="13" fontWeight="700" textAnchor="middle" fill="currentColor">$</text></svg>
    );
    default: return null;
  }
}

// x/y are % of the spine column; the epic thread runs at x=30. Edges are
// drawn centre-to-centre and painted under the nodes, exactly like the board.
// Each node carries the tooltip the dashboard shows for it (type tag,
// confidence, name, evidence line) plus a one-line explainer for visitors who
// have never seen the board. `tip` pins the tooltip's edge for nodes near the
// column sides. Copy stays dash-free for the runtime brand normalizer.
const SPINE_CONF = { teal: 'Traced', amber: 'Partial', red: 'Untraced' };

const SPINE_NODES = [
  { x: 30, y: 6,  s: 42, type: 'epic',      tone: 'teal',   ring: 'teal',  label: 'Operator access & SSO',
    tag: 'Epic', sub: 'v2.6 · 6 issues',
    desc: 'A baseline epic: every work item under it traces to the client objective.' },
  { x: 66, y: 12, s: 34, type: 'item',      tone: 'slate',  ring: 'teal',
    tag: 'Work item', title: 'Access: TOTP fallback', sub: 'PORT 310 · Apr 06',
    desc: 'A ticket pulled into evidence, linked to its epic and the PRs that closed it.' },
  { x: 30, y: 21, s: 42, type: 'epic',      tone: 'teal',   ring: 'amber', warn: 'amber', label: 'Console preferences',
    tag: 'Epic', sub: 'v2.7 · acceptance criteria changed',
    desc: 'Acceptance criteria moved mid cycle, so its trace is partial and flagged.' },
  { x: 14, y: 30, s: 46, type: 'objective', tone: 'blue',   ring: 'teal',  label: 'Improve fleet operations', tip: 'start',
    tag: 'Client objective', title: 'Improve fleet operations visibility', sub: 'Q2 OKR · Farhaven Industries',
    desc: 'The client goal every chain of evidence leads back to.' },
  { x: 30, y: 36, s: 42, type: 'epic',      tone: 'teal',   ring: 'teal',  label: 'Manifest export',
    tag: 'Epic', sub: 'v2.6 · shipped',
    desc: 'Shipped as planned: tickets, PRs and release all connected.' },
  { x: 64, y: 31, s: 34, type: 'pr',        tone: 'purple', ring: 'teal',
    tag: 'Pull request', title: 'fix(manifest): stream export chunks', sub: 'PR #2188 · Apr 17',
    desc: 'Git evidence: the code change tied to its ticket and the release it shipped in.' },
  { x: 30, y: 50, s: 42, type: 'epic',      tone: 'teal',   ring: 'amber', warn: 'amber', label: 'Alert routing',
    tag: 'Epic', sub: 'v2.7 · added after scope lock',
    desc: 'Added after the lock. This is where the $8,600 expansion starts.' },
  { x: 56, y: 46, s: 32, type: 'meeting',   tone: 'amber',  ring: 'amber',
    tag: 'Meetings', title: 'Clarification cluster', sub: 'CAL 19 · 5 sessions · 8.2h',
    desc: 'Calendar evidence: unplanned coordination time, costed like any other work.' },
  { x: 86, y: 40, s: 48, type: 'cost',      tone: 'blue',   ring: 'amber', label: '$8,600', money: true, tip: 'end',
    tag: 'Cost driver', title: 'Scope expansion', sub: '$8,600 above the locked baseline',
    desc: 'Where the added scope lands as money, ready for the invoice conversation.' },
  { x: 30, y: 65, s: 42, type: 'epic',      tone: 'teal',   ring: 'teal',  label: 'Telemetry performance',
    tag: 'Epic', sub: 'v2.8 · shipped',
    desc: 'Shipped and fully traced, from ticket to release.' },
  { x: 64, y: 58, s: 38, type: 'release',   tone: 'green',  ring: 'teal',  label: 'v2.7',
    tag: 'Release', title: 'Release v2.7', sub: 'Apr 16 · console preferences',
    desc: 'A release cut: everything inside it maps back to items and epics.' },
  { x: 50, y: 76, s: 34, type: 'pr',        tone: 'purple', ring: 'red',   warn: 'red',
    tag: 'Pull request', title: 'chore: console misc cleanup', sub: 'PR #2207 · no ticket',
    desc: 'Work with no baseline link. ScopeWorth flags it instead of losing it.' },
  { x: 84, y: 63, s: 42, type: 'outcome',   tone: 'green',  ring: 'teal',  label: '+21% operator adoption', tip: 'end',
    tag: 'Outcome', sub: 'After the Apr 16 release',
    desc: 'The measured client result the delivery chain ends in.' },
  { x: 84, y: 84, s: 38, type: 'release',   tone: 'green',  ring: 'teal',  label: 'v2.8', tip: 'end',
    tag: 'Release', title: 'Release v2.8', sub: 'Apr 23 · telemetry pass',
    desc: 'The final cut of the quarter, traced end to end.' },
];

const SPINE_EDGES = [
  { d: 'M30 6 C 45 4.5, 56 8, 66 12' },
  { d: 'M66 12 C 52 15, 42 18, 30 21' },
  { d: 'M14 30 C 20 32, 25 34, 30 36' },
  { d: 'M30 36 C 44 34.5, 53 33, 64 31' },
  { d: 'M64 31 C 65 40, 65 49, 64 58' },
  { d: 'M30 50 C 40 48.7, 46 47.3, 56 46' },
  { d: 'M56 46 C 67 44.5, 76 42, 86 40', flag: 'amber' },
  { d: 'M30 50 C 52 53, 72 47, 86 40', flag: 'amber' },
  { d: 'M64 58 C 57 63, 53 69, 50 76', flag: 'red' },
  { d: 'M64 58 C 71 60, 77 61.5, 84 63' },
  { d: 'M30 65 C 50 70, 68 79, 84 84' },
  { d: 'M84 84 C 84 77, 84 70, 84 63' },
];

function ForensicsEvidenceSpine() {
  return (
    <div className="swd-spine" role="group" aria-label="Chain of evidence, from client objective to outcomes">
      <svg className="swd-spine-web" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        {SPINE_EDGES.map((e, i) => (
          <path key={i} className={e.flag ? `is-flag-${e.flag}` : undefined} d={e.d} pathLength="100"/>
        ))}
      </svg>
      {SPINE_NODES.map((n, i) => {
        const conf = SPINE_CONF[n.ring];
        const title = n.title || n.label;
        return (
          <div
            key={i}
            className={`swd-spine-node tone-${n.tone} ring-${n.ring}`}
            style={{ left: `${n.x}%`, top: `${n.y}%`, width: n.s, height: n.s }}
            tabIndex={0}
            role="img"
            aria-label={`${n.tag}: ${title}. ${n.sub}. ${conf}.`}
          >
            <span className="swd-spine-dot" aria-hidden="true">
              <SpineGlyph type={n.type}/>
              {n.warn ? <span className={`swd-spine-warn is-${n.warn}`}>!</span> : null}
            </span>
            {n.label ? <span className={`swd-spine-node-label${n.money ? ' is-money' : ''}`} aria-hidden="true">{n.label}</span> : null}
            <span className={`swd-spine-tip${n.tip ? ` is-${n.tip}` : ''}`} aria-hidden="true">
              <span className="swd-spine-tip-top">
                <span className="swd-spine-tip-type">{n.tag}</span>
                <span className={`swd-spine-tip-conf is-${n.ring}`}>{conf}</span>
              </span>
              <span className="swd-spine-tip-title">{title}</span>
              <span className="swd-spine-tip-sub">{n.sub}</span>
              <span className="swd-spine-tip-desc">{n.desc}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function ForensicStorySection() {
  return (
    <section className="sw-section swd-section" id="forensics">
      <div className="sw-container">
        <div className="swd-forensics-split">
          <div className="swd-forensics-main">
            <div className="sw-section-head swd-forensics-head">
              <div className="sw-eyebrow swd-blue">{tx('widgets.forensics.eyebrow')}</div>
              <h2 className="sw-h2 swd-h2">{tx('widgets.forensics.title')}</h2>
              {/* The lead carries the deep-dive argument too, so the head flows
                  straight into the CTA and player below. */}
              <p className="sw-lead">{tx('widgets.forensics.lead')} {tx('widgets.forensics.deepDive.paragraphs')?.[1]}</p>
            </div>
            <ForensicsDeepDiveCard/>
          </div>
          <ForensicsEvidenceSpine/>
        </div>
      </div>
    </section>
  );
}

// Product: AI economics
export function AIEconomicsSection() {
  return (
    <section className="sw-section swd-section">
      <div className="sw-container">
        <div className="sw-section-head sw-section-head-center">
          <div className="sw-eyebrow swd-purple">{tx('widgets.aiEconomics.eyebrow')}</div>
          <h2 className="sw-h2 swd-h2">{tx('widgets.aiEconomics.title')}</h2>
          <p className="sw-lead">{tx('widgets.aiEconomics.lead')}</p>
        </div>
        <BrowserFrame url="app.scopeworth.app/farhaven/ai">
          <div className="swd-split-2">
            <AICycleTime/>
            <ReviewOverheadBars/>
          </div>
        </BrowserFrame>
      </div>
    </section>
  );
}

// Product / Integrations / Security: confidence gauge
export function ConfidenceSection() {
  return (
    <section className="sw-section swd-section">
      <div className="sw-container">
        <div className="sw-section-head sw-section-head-center">
          <div className="sw-eyebrow swd-teal">{tx('widgets.confidence.eyebrow')}</div>
          <h2 className="sw-h2 swd-h2">{tx('widgets.confidence.title')}</h2>
          <p className="sw-lead">{tx('widgets.confidence.lead')}</p>
        </div>
        <BrowserFrame url="app.scopeworth.app/farhaven/confidence">
          <ConfidenceGaugeWidget/>
        </BrowserFrame>
      </div>
    </section>
  );
}

// Integrations: connected tools
export function ConnectedToolsSection() {
  return (
    <section className="sw-section swd-section" id="connected-tools">
      <div className="sw-container">
        <div className="sw-section-head sw-section-head-center">
          <div className="sw-eyebrow swd-blue">{tx('widgets.connectedTools.eyebrow')}</div>
          <h2 className="sw-h2 swd-h2">{tx('widgets.connectedTools.title')}</h2>
        </div>
        <BrowserFrame url="app.scopeworth.app/farhaven/sources">
          <ConnectedTools/>
        </BrowserFrame>
      </div>
    </section>
  );
}

// ---------- 14. Scenario card (capacity cost simulator) ----------
export function ScenarioCard({ scenario }) {
  const badgeTone = scenario.badge === 'rec' ? 'teal' : scenario.badge === 'warn' ? 'amber' : null;
  const badgeLabel = scenario.badge === 'rec' ? tx('swd.scenario.recommended') : scenario.badge === 'warn' ? tx('swd.scenario.fasterRisk') : null;
  return (
    <div className={`swd-scen is-${scenario.badge || 'base'}`}>
      <div className="swd-scen-h">
        <div>
          <div className="swd-scen-name">{scenario.name}</div>
          <div className="swd-scen-sub">{scenario.sub}</div>
        </div>
        {badgeLabel && <Tag tone={badgeTone}>{badgeLabel}</Tag>}
      </div>
      <div className="swd-scen-stats">
        <div className="swd-scen-stat">
          <div className="swd-scen-stat-l">{tx('swd.scenario.weeks')}</div>
          <div className="swd-scen-stat-v tabular">{scenario.weeks}</div>
        </div>
        <div className="swd-scen-stat">
          <div className="swd-scen-stat-l">{tx('swd.scenario.totalCost')}</div>
          <div className="swd-scen-stat-v tabular">{scenario.cost}</div>
        </div>
      </div>
      <p className="swd-scen-tradeoffs">{scenario.tradeoffs}</p>
    </div>
  );
}

// ---------- 15. Scenario compare grid ----------
export function ScenarioCompareGrid({ scenarios = SAMPLE.simulator.scenarios, perWeek = SAMPLE.simulator.perWeekSaved }) {
  const [ref, t, started] = useInViewAnim({ duration: 1200 });
  const animated = scenarios.map(s => ({
    ...s,
    weeks: tweenValueString(s.weeks, t),
    cost:  tweenValueString(s.cost, t),
  }));
  return (
    <div ref={ref}>
      <div className="swd-scen-grid">
        {animated.map((s, i) => (
          <div key={s.id}
            className={`swd-anim-rise ${started ? 'is-in' : ''}`}
            style={{ transitionDelay: `${i * 90}ms` }}>
            <ScenarioCard scenario={s}/>
          </div>
        ))}
      </div>
      <div className="swd-scen-perweek">
        <span className="swd-eyebrow">{tx('swd.scenario.perWeekTitle')}</span>
        <span><strong>{tx('swd.scenario.perWeekA')}</strong> <span className="tabular">{perWeek.a}{tx('swd.scenario.perWeekSuffix')}</span></span>
        <span><strong>{tx('swd.scenario.perWeekB')}</strong> <span className="tabular">{perWeek.b}{tx('swd.scenario.perWeekSuffix')}</span></span>
      </div>
    </div>
  );
}

// ---------- 16. Assumptions panel (collapsible) ----------
export function AssumptionsPanel({ items = SAMPLE.simulator.assumptions, defaultOpen = false, showFormulas = false }) {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div className="swd-assume">
      <button className="swd-assume-toggle" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <span className="swd-eyebrow">{tx('swd.assumptions.title')}</span>
        <span aria-hidden="true">{open ? tx('swd.assumptions.close') : '+'}</span>
      </button>
      {open && (
        <ul className="swd-assume-list">
          {items.map((a, i) => (
            <li key={i}>
              <div className="swd-assume-l">{a.l}</div>
              <div className="swd-assume-d">{a.d}</div>
              {showFormulas && a.f && <div className="swd-assume-f tabular">{a.f}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------- 17. Saved-scenarios picker ----------
function SavedScenariosStrip({ items = SAMPLE.simulator.saved }) {
  return (
    <div className="swd-saved">
      <span className="swd-eyebrow">{tx('swd.saved.title')}</span>
      <div className="swd-saved-row">
        {items.map(s => (
          <div key={s.id} className="swd-saved-card">
            <div className="swd-saved-h">
              <span className="swd-saved-proj">{s.project}</span>
              <Tag tone={s.status === 'live' ? 'teal' : s.status === 'discovery' ? 'amber' : 'blue'}>{s.status === 'live' ? tx('swd.saved.stLive') : s.status === 'discovery' ? tx('swd.saved.stDiscovery') : tx('swd.saved.stClosed')}</Tag>
            </div>
            <div className="swd-saved-name">{s.name}</div>
            <div className="swd-saved-meta tabular">{s.weeks} · {s.cost} · {s.best}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- 18. Provenance event row ----------
export function ProvenanceEventRow({ event = SAMPLE.provenance.event }) {
  const [ref, , started] = useInViewAnim();
  return (
    <div ref={ref} className="swd-prov">
      <div className="swd-prov-h">
        <span className="swd-prov-when">{event.when}</span>
        <span className="swd-prov-sep">·</span>
        <span className="swd-prov-connector">{event.connector}</span>
        <span className="swd-prov-sep">·</span>
        <span className="swd-prov-ref tabular">{event.ref}</span>
      </div>
      <div className="swd-prov-summary">{event.summary}</div>
      <div className="swd-prov-impact-h swd-eyebrow">Impact on this report</div>
      <ul className="swd-prov-impacts">
        {event.impacts.map((im, i) => (
          <li key={i}
            className={`is-${im.tone} swd-anim-slide ${started ? 'is-in' : ''}`}
            style={{ transitionDelay: `${120 + i * 110}ms` }}>
            <span>{im.l}</span>
            <span className="tabular">{im.v}</span>
          </li>
        ))}
      </ul>
      <div className="swd-prov-actions">
        <a className="swd-prov-link" href="#provenance-product">Open evidence
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
        </a>
      </div>
    </div>
  );
}

// ---------- 19. Saved views list (audit) ----------
export function SavedViewsList({ items = SAMPLE.provenance.savedViews }) {
  return (
    <div className="swd-views">
      <div className="swd-eyebrow swd-views-h">Saved views</div>
      <ul className="swd-views-list">
        {items.map(v => (
          <li key={v.id}>
            <span className="swd-views-l">{v.label}</span>
            <span className="swd-views-m">{v.meta}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------- 20. Scope baseline strip (timeline lane) ----------
export function ScopeBaselineStrip({ events = SAMPLE.timeline }) {
  const [ref, , started] = useInViewAnim();
  return (
    <Frame eyebrow="Scope baseline" title="The month, on one lane" sub={`${SAMPLE.scopeLock} scope lock → Apr 30 ship`} innerRef={ref}>
      <div className={`swd-lane ${started ? 'is-in' : ''}`}>
        {events.map((ev, i) => (
          <div key={i}
            className={`swd-lane-pill is-${ev.kind} swd-anim-rise ${started ? 'is-in' : ''}`}
            style={{ transitionDelay: `${i * 80}ms` }}>
            <span className="swd-lane-date">{ev.date}</span>
            <span className="swd-lane-tag">{ev.tag}</span>
            {ev.cost && <span className="swd-lane-cost tabular">{ev.cost}</span>}
          </div>
        ))}
      </div>
    </Frame>
  );
}

// ---------- 21. Integration health strip ----------
export function IntegrationHealthStrip({ items = SAMPLE.integrationHealth }) {
  return (
    <div className="swd-health-strip">
      {items.map((k, i) => (
        <div key={i} className={`swd-health-tile is-${k.tone}`}>
          <div className="swd-eyebrow">{k.l}</div>
          <div className="swd-health-tile-v tabular">{k.v}</div>
        </div>
      ))}
    </div>
  );
}

// ---------- 22. Dashboard surface tile (9-tile product hero grid) ----------
export function DashboardSurfaceTile({ tile, onActivate }) {
  return (
    <a className={`swd-surface is-${tile.tone}`} href={`#${tile.id}`}
       onClick={onActivate ? (e) => { e.preventDefault(); onActivate(tile); } : undefined}>
      <div className="swd-eyebrow">{tile.label}</div>
      <div className="swd-surface-v tabular">{tile.value}</div>
      <div className="swd-surface-n">{tile.note}</div>
    </a>
  );
}

export function DashboardSurfaceGrid({ tiles = SAMPLE.surfaces }) {
  const [ref, , started] = useInViewAnim();
  return (
    <div ref={ref} className="swd-surface-grid">
      {tiles.map((tile, i) => (
        <div key={tile.id}
          className={`swd-anim-rise ${started ? 'is-in' : ''}`}
          style={{ transitionDelay: `${i * 60}ms` }}>
          <DashboardSurfaceTile tile={tile}/>
        </div>
      ))}
    </div>
  );
}

// ---------- 23. Month-in-agency diagram (About page) ----------
export function MonthInAgencyDiagram() {
  const steps = [
    { t: 'Onboarding', n: '01', sub: 'Connect tools · map clients', tone: 'blue'   },
    { t: 'Bid',        n: '02', sub: 'Capacity Cost Simulator',     tone: 'purple' },
    { t: 'Deliver',    n: '03', sub: 'Forensics · scope baseline',  tone: 'blue'   },
    { t: 'Defend',     n: '04', sub: 'Invoice Defense · waterfall', tone: 'amber'  },
    { t: 'Renew',      n: '05', sub: 'Retainer review · narrative', tone: 'teal'   },
  ];
  return (
    <div className="swd-month">
      {steps.map((s, i) => (
        <React.Fragment key={s.t}>
          <div className={`swd-month-step is-${s.tone}`}>
            <div className="swd-month-num">{s.n}</div>
            <div className="swd-month-t">{s.t}</div>
            <div className="swd-month-sub">{s.sub}</div>
          </div>
          {i < steps.length - 1 && <span className="swd-month-arrow" aria-hidden="true">→</span>}
        </React.Fragment>
      ))}
    </div>
  );
}

// ============================================================
// Net-new section wrappers
// ============================================================

// Home/Product: Capacity Cost Simulator
export function CapacitySimulatorSection() {
  return (
    <section className="sw-section swd-section" id="simulator">
      <div className="sw-container">
        <div className="sw-section-head sw-section-head-center">
          <div className="sw-eyebrow swd-purple">{tx('swd.simulatorSection.eyebrow')}</div>
          <h2 className="sw-h2 swd-h2">{tx('swd.simulatorSection.title')}</h2>
          <p className="sw-lead">{tx('swd.simulatorSection.lead')}</p>
        </div>
        <div className="swd-stack">
          <BrowserFrame url="app.scopeworth.app/farhaven/simulator">
            <ScenarioCompareGrid/>
            <AssumptionsPanel/>
          </BrowserFrame>
          <div className="swd-section-actions">
            <a className="sw-btn sw-btn-primary sw-btn-lg" href={localizedHref('/product#simulator')}>
              <span className="sw-btn-label">{tx('swd.simulatorSection.seeMath')}</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
            </a>
            <button className="sw-btn sw-btn-ghost-dark sw-btn-lg" onClick={() => window.openSampleReport && window.openSampleReport('home-simulator')}>
              <span className="sw-btn-label">{tx('swd.simulatorSection.downloadBid')}</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export function CapacitySimulatorProductSection() {
  return (
    <section className="sw-section swd-section" id="simulator-product">
      <div className="sw-container">
        <div className="sw-section-head sw-section-head-center">
          <div className="sw-eyebrow swd-purple">{tx('widgets.simulator.eyebrow')}</div>
          <h2 className="sw-h2 swd-h2">{tx('widgets.simulator.title')}</h2>
          <p className="sw-lead">{tx('widgets.simulator.lead')}</p>
        </div>
        <BrowserFrame url="app.scopeworth.app/farhaven/simulator">
          <ScenarioCompareGrid/>
          <AssumptionsPanel defaultOpen={true} showFormulas={true}/>
          <SavedScenariosStrip/>
        </BrowserFrame>
      </div>
    </section>
  );
}

// Home: Report Provenance (audit trail repositioned)
export function ReportProvenanceSection() {
  return (
    <section className="sw-section swd-section" id="provenance">
      <div className="sw-container">
        <div className="sw-section-head sw-section-head-center">
          <div className="sw-eyebrow swd-blue">Report provenance</div>
          <h2 className="sw-h2 swd-h2">Every number in your client report has a paper trail.</h2>
          <p className="sw-lead">ScopeWorth records every connector event that contributed to a metric. When a client questions a cost, drill in to the exact tickets, PRs, meetings, and time entries behind it.</p>
        </div>
        <div className="swd-split-2">
          <BrowserFrame url="app.scopeworth.app/farhaven/audit">
            <ProvenanceEventRow/>
          </BrowserFrame>
          <div className="swd-prov-aside">
            <div className="swd-eyebrow swd-blue">What the audit trail covers</div>
            <ul className="swd-prov-bullets">
              {SAMPLE.provenance.bullets.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
            <div className="swd-section-actions" style={{ marginTop: 14 }}>
              <a className="sw-btn sw-btn-primary" href={localizedHref('/product#provenance-product')}>
                See the audit trail
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
              </a>
              <a className="sw-btn sw-btn-ghost-dark" href="#confidence">Learn about report confidence</a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Expose for any non-module callers (parity with Sections.jsx pattern)
// Guarded: the build-time prerender imports this module in Node, where
// `window` is absent. Same pattern as Forms.jsx and CookieConsent.jsx.
if (typeof window !== 'undefined') Object.assign(window, {
  BrowserFrame,
  KpiCards, DeliveryInvestmentFlow, CostDriverBreakdown, WhatChangedInsights,
  DeliveryProofMap, ForensicTimeline, ClientQuestionCard, CostDriversTable,
  InvoiceWaterfall, AICycleTime, ReviewOverheadBars, ConfidenceGaugeWidget,
  ConnectedTools,
  ScenarioCard, ScenarioCompareGrid, AssumptionsPanel,
  ProvenanceEventRow, SavedViewsList,
  ScopeBaselineStrip, IntegrationHealthStrip,
  DashboardSurfaceTile, DashboardSurfaceGrid,
  MonthInAgencyDiagram,
  ReportAnatomySection, InvoiceDefenseSection, ForensicStorySection,
  AIEconomicsSection, ConfidenceSection, ConnectedToolsSection,
  CapacitySimulatorSection, CapacitySimulatorProductSection,
  ReportProvenanceSection,
});
